import React, { memo, useContext, useState, useRef, useEffect } from 'react';
import isEqual from 'lodash.isequal';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import getSuitCSSClasses from '@/utils/getSuitCSSClasses';
import { Checkbox, StructuredListing } from '@hz-design-system/web-ui';
import { ConvertedListingProps, ListingProps } from '@/client/types/Listings';
import VIEW_OPTIONS from '@/constants/viewOptions';
import VERTICALS from '@/constants/verticals';
import trackingKeys from '@/client/constants/analytics/trackingKeys';
import distanceKmFormatter from '@/client/utils/formatters/distanceKmFormatter';
import priceFormatter from '@/client/utils/formatters/priceFormatter';
import trustIndicatorsFormatter from '@/client/utils/formatters/trustIndicatorsFormatter.ts';
import { sellerUrlGenerator } from '@/utils/links/createSellerLink';
import { linkGeneratorHelper } from '@/utils/links/linkGeneratorHelper';
import trackEvent from '@/client/utils/analytics/trackEvent';
import toggleFavorite from '@/client/actions/favorites/toggleFavorite';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import ListingListViewContentCars from '@/client/components/Listings/molecules/ListingListViewContentCars';
import locationFormatter from '@/client/utils/formatters/locationFormatter';
import ListingListViewContentHorizontal from '@/client/components/ListingsNew/ListingListViewContentHorizontal/ListingListViewContentHorizontal.tsx';
import { formatImageUrl } from '@ecg-marktplaats/media-util-js-lib';
import { MAIN_IMAGE_SIZE_GALLERY } from '@/client/constants/listings.ts';
import imageFormatter from '@/client/utils/formatters/imageFormatter.ts';
import { setCookie } from '@/client/utils/cookies';
import enrichVipUrlWithTrackingData from '@/utils/links/enrichVipUrlWithTrackingData';
import experiments from '@/utils/labs/experiments';
import BuyerInfoModal from '../../FavouritesConnect/BuyerInfoModal.tsx';

const INITIAL_LISTINGS_COUNT = 2;

const trackGaEventForBegin = (isFavorite: boolean) => {
  trackEvent({
    eventAction: isFavorite
      ? trackingKeys.FAVORITE.EVENT_ACTION.REMOVE_BEGIN
      : trackingKeys.FAVORITE.EVENT_ACTION.ADD_BEGIN,
    eventLabel: trackingKeys.FAVORITE.EVENT_LABEL,
  });
};

const trackGaEventForSuccess = (isFavorite: boolean, extraMetrics?: Record<string, any>) => {
  trackEvent({
    eventAction: isFavorite
      ? trackingKeys.FAVORITE.EVENT_ACTION.REMOVE_SUCCESS
      : trackingKeys.FAVORITE.EVENT_ACTION.ADD_SUCCESS,
    eventLabel: trackingKeys.FAVORITE.EVENT_LABEL,
    eventMetric: extraMetrics,
  });
};

const getElementProps = (props: ListingProps, i18n: any): ConvertedListingProps => {
  const { t } = i18n;
  const { priceInfo, date, location, trustIndicators, sellerInformation, verticals = [], reserved = false } = props;
  const sellerInfo = {
    id: sellerInformation?.sellerId,
    name: sellerInformation?.sellerName,
  };

  priceInfo.suppressZeroCents = verticals.includes(VERTICALS.CARS) || verticals.includes(VERTICALS.AUTOMOTIVE);

  return {
    ...props,
    location: {
      label: locationFormatter(location, i18n),
      distance: distanceKmFormatter(location.distanceMeters || 0),
    },
    price: t(priceFormatter(priceInfo, reserved), priceInfo),
    date: date,
    sellerInformation: {
      ...sellerInformation,
      soiUrl: sellerUrlGenerator(sellerInfo),
    },
    ...(trustIndicators && {
      trustIndicators: trustIndicatorsFormatter(trustIndicators, i18n),
    }),
  };
};

const Listing: React.FC<ListingProps> = ({
  selectAction,
  isSelected = false,
  isSelectable = false,
  isFavorited = false,
  isBottomBlock = false,
  variant = '',
  vertical = '',
  otherSellerListings,
  searchRequestObject,
  thinContent,
  isSOIPage,
  index = 0,
  traits,
  ...props
}) => {
  const i18n = useI18nContext();
  const { t } = i18n;
  const {
    xsrfToken,
    userDetails: { isLoggedIn },
    labsConfig,
    favouritesConnect,
  } = useContext(EnvironmentContext);
  const categories = searchRequestObject?.categories;
  const isCarsVariant = vertical === VERTICALS.CARS;
  const verticalVariant = vertical ? `-${vertical}` : '';
  const { kind, sellerInformation, itemId, extendedAttributes, categorySpecificDescription, priorityProduct } = props;
  const viewKind = kind === VIEW_OPTIONS.LIST_VIEW ? 'list-item' : 'gallery-item';
  const isListItem = viewKind === 'list-item';
  const elementProps = getElementProps({ ...props, index }, i18n);
  const [isFavorite, setIsFavorite] = useState<boolean>(isFavorited);
  const handleSelectAction = () => selectAction && selectAction(itemId, !isSelected);
  const loadingType = isBottomBlock || index > INITIAL_LISTINGS_COUNT ? 'lazy' : 'eager';
  const decodingType = isBottomBlock || index > INITIAL_LISTINGS_COUNT ? 'async' : undefined;
  const fetchPriority = isBottomBlock || index > 0 ? 'low' : 'high';
  const hasCarOptions = extendedAttributes?.find((x) => x.key === 'options');
  const isCarsExtendedContentAvailable = isCarsVariant && !!hasCarOptions;
  const isExtendedContentAvailable = !isCarsVariant && (!!extendedAttributes?.length || !!categorySpecificDescription);

  const shouldDisplayExtendedContent =
    !isSOIPage && !isBottomBlock && (isCarsExtendedContentAvailable || isExtendedContentAvailable);

  const images = imageFormatter(elementProps.pictures ?? [], elementProps.title, categories, extendedAttributes);
  const { vipUrl, trackingData, correlationId, pageLocation } = elementProps;
  const { isBot } = useContext(EnvironmentContext);
  const [href, setHref] = useState(vipUrl);
  const [showFavouriteInfoModal, setShowFavouriteInfoModal] = useState(false);

  const { isFavouritesConnectEnabled } = experiments({
    labsConfig,
  });

  useEffect(() => {
    if (vipUrl && !isBot) {
      setHref(
        enrichVipUrlWithTrackingData({
          vipUrl,
          trackingData,
          pageLocation,
        }),
      );
    }
  }, [vipUrl, isBot, trackingData, pageLocation]);

  const closeToast = useRef<any>(null);
  let otherSellerUrl: string | undefined;
  if (isListItem && otherSellerListings && otherSellerListings.totalSellerListingsResults > 1) {
    otherSellerUrl = linkGeneratorHelper({
      searchRequestObject: {
        ...searchRequestObject,
        seller: {
          id: sellerInformation.sellerId,
          name: sellerInformation.sellerName,
        },
      },
    });
  }

  const onFavorite = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.preventDefault();
    }

    if (!isLoggedIn) {
      if (typeof window === 'undefined') return;
      window.location.assign(`/account/login.html?target=${encodeURIComponent(window.location.href)}`);
      return;
    }

    setIsFavorite(!isFavorite);
    trackGaEventForBegin(isFavorite);

    if (closeToast.current) {
      closeToast.current();
    }

    const [response, { default: showToastNotification, dismissToast }] = await Promise.all([
      toggleFavorite({ itemId, xsrfToken, favorite: !isFavorite }),
      import(/* webpackChunkName: "toastNotification" */ '../../Toast/ToastNotification.tsx'),
    ]);

    closeToast.current = dismissToast;

    if (isFavorite !== response.isFavorite) {
      trackGaEventForSuccess(isFavorite, {
        ...(response.isFavorite &&
          isFavouritesConnectEnabled && {
            seller_contact_enabled: favouritesConnect?.sellerContactEnabled,
          }),
      });

      showToastNotification({
        type: 'success',
        description: t(
          isFavorite ? 'listing.favourite.notifications.unfavoriting' : 'listing.favourite.notifications.favoriting',
        ),
      });
    } else {
      showToastNotification({
        type: 'error',
        description: t('page.error.error_global_ajax'),
      });
    }

    // actual update
    setIsFavorite(response.isFavorite);

    setShowFavouriteInfoModal(response.isFavorite);
  };

  const createCorrelationIdCookieAndStoreScrollPos = () => {
    if (typeof window === 'undefined') return;

    if (correlationId) {
      setCookie('searchRequestId', correlationId);
    }

    sessionStorage.setItem(
      'lrp_scroll_store',
      JSON.stringify({
        href: window.location.href,
        pos: window.scrollY,
        height: document.querySelector('.hz-Page-body')?.scrollHeight,
      }),
    );
  };

  const handleContextMenu = () => {
    if (vipUrl) {
      setHref(
        enrichVipUrlWithTrackingData({
          vipUrl,
          trackingData,
          pageLocation,
          correlationId,
        }),
      );
    }
  };

  const sellerWebsiteUrlClickHandler = () => () => {
    trackEvent({
      eventAction: trackingKeys.SELLER_WEBSITE_URL.EVENT_ACTION,
      eventLabel: trackingKeys.SELLER_WEBSITE_URL.EVENT_LABEL,
    });
  };

  return (
    <li
      className={
        isListItem
          ? getSuitCSSClasses({
              namespace: 'hz',
              name: 'Listing',
              modifiers: [`${viewKind}${verticalVariant}`, variant],
            })
          : undefined
      }
    >
      {isSelectable && (
        <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Listing-select-container' })}>
          <Checkbox value="true" isChecked={isSelected} onChange={handleSelectAction} labelText={''} />
        </div>
      )}

      {isListItem && !isCarsVariant && (
        <ListingListViewContentHorizontal
          {...elementProps}
          attributes={elementProps.attributes || []}
          extendedAttributes={elementProps.extendedAttributes || []}
          isBottomBlock={isBottomBlock}
          fetchPriority={fetchPriority}
          loadingType={loadingType}
          decodingType={decodingType}
          categories={categories}
          isFavorite={isFavorite}
          onFavorite={onFavorite}
          thinContent={thinContent}
          otherSellerUrl={otherSellerUrl}
          otherSellerListings={otherSellerListings}
          shouldDisplayExtendedContent={shouldDisplayExtendedContent}
        />
      )}

      {isListItem && isCarsVariant && (
        <ListingListViewContentCars
          {...elementProps}
          attributes={elementProps.attributes || []}
          extendedAttributes={elementProps.extendedAttributes || []}
          fetchPriority={fetchPriority}
          loadingType={loadingType}
          decodingType={decodingType}
          isFavorite={isFavorite}
          onFavorite={onFavorite}
          thinContent={thinContent}
          priceType={props.priceInfo.priceType}
          categories={categories}
          otherSellerUrl={otherSellerUrl}
          otherSellerListings={otherSellerListings}
        />
      )}

      {!isListItem && isBottomBlock && (
        <StructuredListing
          title={elementProps.title}
          price={elementProps.price}
          imageSrc={formatImageUrl(images && images[0]?.url, MAIN_IMAGE_SIZE_GALLERY, images && images[0]?.sizes)}
          imageLoading={loadingType}
          decoding={decodingType}
          isMaskedLink={elementProps.thinContent ? true : false}
          onHeart={onFavorite}
          isFavorite={isFavorite}
          showFav={true}
          favAriaLabel={
            isFavorite ? t('lrp.listing.favourite.ariaLabel.remove') : t('lrp.listing.favourite.ariaLabel.add')
          }
          data-testid="structured-listing"
          details={{
            externalLinkProps: {
              href: sellerInformation.sellerWebsiteUrl!,
              onClick: () => sellerWebsiteUrlClickHandler(),
              linkLabel: t('listings.visit'),
            },
          }}
          badges={{
            opvalStickerText: elementProps.opvalStickerText,
            infoText: elementProps.urgencyFeatureActive ? t('listings.moet-nu-weg') : '',
          }}
          aspectRatio="square"
          linkProps={{
            href: href,
            onClick: () => createCorrelationIdCookieAndStoreScrollPos(),
            onContextMenu: handleContextMenu,
          }}
        />
      )}

      {!isListItem && !isBottomBlock && (
        <StructuredListing
          title={elementProps.title}
          price={elementProps.price}
          imageSrc={formatImageUrl(images && images[0]?.url, MAIN_IMAGE_SIZE_GALLERY, images && images[0]?.sizes)}
          imageLoading={loadingType}
          decoding={decodingType}
          isMaskedLink={elementProps.thinContent ? true : false}
          onHeart={onFavorite}
          isFavorite={isFavorite}
          showFav={true}
          favAriaLabel={
            isFavorite ? t('lrp.listing.favourite.ariaLabel.remove') : t('lrp.listing.favourite.ariaLabel.add')
          }
          data-testid="structured-listing"
          details={{
            priorityProduct: priorityProduct !== 'NONE' ? t(`priorityProduct.${priorityProduct}`) : false,
            location: elementProps.location,
            date: elementProps.date,
            externalLinkProps:
              sellerInformation && sellerInformation.showWebsiteUrl
                ? {
                    href: sellerInformation.sellerWebsiteUrl,
                    onClick: () => sellerWebsiteUrlClickHandler(),
                    linkLabel: t('listings.visit'),
                  }
                : // TODO fix this
                  ({} as any),
          }}
          badges={{
            opvalStickerText: elementProps.opvalStickerText,
            infoText: elementProps.urgencyFeatureActive ? t('listings.moet-nu-weg') : '',
          }}
          aspectRatio="square"
          linkProps={{
            href: href,
            onClick: () => createCorrelationIdCookieAndStoreScrollPos(),
            onContextMenu: handleContextMenu,
          }}
        />
      )}

      {isFavouritesConnectEnabled && showFavouriteInfoModal && (
        <BuyerInfoModal
          onClose={() => setShowFavouriteInfoModal(false)}
          onTrack={(params) => {
            trackEvent({
              eventAction: params.action,
              eventLabel: params.label,
              eventMetric: {
                event_category: params.category,
              },
            });
          }}
        />
      )}
    </li>
  );
};

export default memo(Listing, (prevProps, nextProps) => isEqual(prevProps, nextProps));
